import React, { useEffect, useState } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  Typography,
  Stack,
  Box,
  Chip,
  IconButton,
  InputAdornment,
} from "@mui/material";

import RestaurantIcon from "@mui/icons-material/Restaurant";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FreeBreakfastIcon from "@mui/icons-material/FreeBreakfast";
import LunchDiningIcon from "@mui/icons-material/LunchDining";
import DinnerDiningIcon from "@mui/icons-material/DinnerDining";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

/* =========================================================
   COMMON STYLES
========================================================= */

const compactFieldSx = {
  "& .MuiOutlinedInput-root": {
    height: 38,
    borderRadius: 1.5,
    backgroundColor: "#fff",
  },

  "& .MuiInputBase-input": {
    fontSize: "0.82rem",
    py: 0.75,
  },

  "& .MuiInputLabel-root": {
    fontSize: "0.78rem",
  },

  "& .MuiInputLabel-root.Mui-focused": {
    fontSize: "0.78rem",
  },
};

/* =========================================================
   HELPERS
========================================================= */

const toNumber = (value, fallback = 0) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return fallback;
  }

  const number = Number(value);

  return Number.isNaN(number)
    ? fallback
    : number;
};

/* =========================================================
   DATE NORMALIZER
========================================================= */

const normalizeDate = (value) => {
  if (!value) {
    return "";
  }

  const stringValue = String(value);

  if (stringValue.includes("T")) {
    return stringValue.split("T")[0];
  }

  return stringValue.substring(0, 10);
};

/* =========================================================
   CREATE MEAL
========================================================= */

const createMeal = (qty = 1) => ({
  selected: true,
  qty: toNumber(qty, 1),
  childQty: 0,
  amount: 0,
});

/* =========================================================
   CREATE DAY
========================================================= */

const createDay = (
  date = "",
  qty = 1,
  hostelId = null
) => ({
  id: null,

  hostelId,

  date: normalizeDate(date),

  mealDate: date || null,

  breakfast: createMeal(qty),
  lunch: createMeal(qty),
  dinner: createMeal(qty),

  extraMeal: "",
  extraAmount: 0,

  totalAmount: 0,
});

/* =========================================================
   CALCULATE DAY TOTAL
========================================================= */

const calculateDayTotal = (day) => {
  const meals = [
    "breakfast",
    "lunch",
    "dinner",
  ];

  const mealTotal = meals.reduce(
    (sum, meal) => {
      const currentMeal = day?.[meal];

      if (!currentMeal?.selected) {
        return sum;
      }

      return (
        sum +
        toNumber(currentMeal.amount, 0)
      );
    },
    0
  );

  const extraAmount = toNumber(
    day?.extraAmount,
    0
  );

  return mealTotal + extraAmount;
};

/* =========================================================
   NORMALIZE ONE MEAL
========================================================= */

const normalizeMeal = (
  item,
  mealName,
  fallbackQty = 1
) => {
  /*
   * Supports frontend nested format:
   *
   * dinner: {
   *   selected: true,
   *   qty: 1,
   *   childQty: 0,
   *   amount: 2334
   * }
   */

  const nestedMeal =
    item?.[mealName];

  if (
    nestedMeal &&
    typeof nestedMeal === "object"
  ) {
    return {
      selected: Boolean(
        nestedMeal.selected
      ),

      qty: toNumber(
        nestedMeal.qty,
        fallbackQty
      ),

      childQty: toNumber(
        nestedMeal.childQty,
        0
      ),

      amount: toNumber(
        nestedMeal.amount,
        0
      ),
    };
  }

  /*
   * Supports backend format:
   *
   * dinnerSelected: true
   * dinnerQty: 1
   * dinnerChildQty: null
   * dinnerAmount: 2334
   */

  return {
    selected: Boolean(
      item?.[
        `${mealName}Selected`
      ]
    ),

    qty: toNumber(
      item?.[`${mealName}Qty`],
      fallbackQty
    ),

    childQty: toNumber(
      item?.[
        `${mealName}ChildQty`
      ],
      0
    ),

    amount: toNumber(
      item?.[`${mealName}Amount`],
      0
    ),
  };
};

/* =========================================================
   NORMALIZE SAVED DAILY DETAIL
========================================================= */

const normalizeSavedDay = (
  item,
  rowData
) => {
  const quantity = toNumber(
    rowData?.count,
    1
  );

  /*
   * IMPORTANT:
   *
   * Check item.date first.
   * Some parent components may already
   * convert mealDate to date.
   */

  const rawDate =
    item?.date ||
    item?.mealDate ||
    "";

  const normalizedDate =
    normalizeDate(rawDate);

  const breakfast =
    normalizeMeal(
      item,
      "breakfast",
      quantity
    );

  const lunch =
    normalizeMeal(
      item,
      "lunch",
      quantity
    );

  const dinner =
    normalizeMeal(
      item,
      "dinner",
      quantity
    );

  const extraMeal =
    item?.extraMeal || "";

  const extraAmount = toNumber(
    item?.extraAmount,
    0
  );

  const normalized = {
    /*
     * Keep existing backend ID.
     */

    id: item?.id ?? null,

    hostelId:
      item?.hostelId ??
      rowData?.hostelId ??
      null,

    date: normalizedDate,

    mealDate:
      item?.mealDate ||
      normalizedDate ||
      null,

    breakfast,

    lunch,

    dinner,

    extraMeal,

    extraAmount,

    totalAmount: 0,
  };

  /*
   * IMPORTANT:
   *
   * Don't depend on backend totalAmount
   * because it can be null.
   *
   * Calculate from meal amounts.
   */

  normalized.totalAmount =
    calculateDayTotal(normalized);

  console.log(
    "NORMALIZED DAY:",
    JSON.stringify(
      normalized,
      null,
      2
    )
  );

  return normalized;
};

/* =========================================================
   COMPONENT
========================================================= */

const DailyMessDetailsDialog = ({
  open,
  onClose,
  row,
  onSaveDetails,
  isEditing = true,
}) => {
  const [details, setDetails] =
    useState([]);

  /* =====================================================
     EFFECT DEPENDENCY VALUES

     IMPORTANT:
     Do not use entire `row` in useEffect dependency.
     This prevents exhaustive-deps warning.
  ===================================================== */

  const rowCount = toNumber(
    row?.count,
    1
  );

  const rowHostelId =
    row?.hostelId ?? null;

  const rowCheckIn =
    row?.checkIn;

  const rowCheckOut =
    row?.checkOut;

  const rowDailyMessDetails =
    row?.dailyMessDetails;

  /* =====================================================
     GRAND TOTAL
  ===================================================== */

  const totalAmount = () => {
    return details.reduce(
      (sum, item) =>
        sum +
        toNumber(
          item?.totalAmount,
          0
        ),
      0
    );
  };

  /* =====================================================
     LOAD DATA
  ===================================================== */

  useEffect(() => {
    if (!open) {
      return;
    }

    /* =================================================
       EXISTING SAVED DAILY MESS DETAILS
    ================================================= */

    if (
      Array.isArray(
        rowDailyMessDetails
      ) &&
      rowDailyMessDetails.length > 0
    ) {
      console.log(
        "DIALOG RECEIVED DETAILS:",
        JSON.stringify(
          rowDailyMessDetails,
          null,
          2
        )
      );

      const savedDetails =
        rowDailyMessDetails.map(
          (item) =>
            normalizeSavedDay(
              item,
              {
                count: rowCount,
                hostelId:
                  rowHostelId,
              }
            )
        );

      console.log(
        "FINAL DETAILS USED BY DIALOG:",
        JSON.stringify(
          savedDetails,
          null,
          2
        )
      );

      setDetails(savedDetails);

      return;
    }

    /* =================================================
       NO EXISTING DETAILS
    ================================================= */

    if (
      !rowCheckIn ||
      !rowCheckOut
    ) {
      setDetails([]);
      return;
    }

    const start = new Date(
      rowCheckIn
    );

    const end = new Date(
      rowCheckOut
    );

    const generatedRows = [];

    while (start <= end) {
      const date =
        start
          .toISOString()
          .split("T")[0];

      generatedRows.push(
        createDay(
          date,
          rowCount,
          rowHostelId
        )
      );

      start.setDate(
        start.getDate() + 1
      );
    }

    setDetails(generatedRows);
  }, [
    open,
    rowCount,
    rowHostelId,
    rowCheckIn,
    rowCheckOut,
    rowDailyMessDetails,
  ]);

  /* =====================================================
     ADD DAY
  ===================================================== */

  const addDay = () => {
    setDetails((previous) => [
      ...previous,

      createDay(
        "",
        rowCount,
        rowHostelId
      ),
    ]);
  };

  /* =====================================================
     DATE CHANGE
  ===================================================== */

  const handleDateChange = (
    index,
    value
  ) => {
    setDetails((previous) => {
      const updated = [
        ...previous,
      ];

      updated[index] = {
        ...updated[index],

        date: value,

        mealDate: value,
      };

      return updated;
    });
  };

  /* =====================================================
     MEAL CHANGE
  ===================================================== */

  const handleMealChange = (
    rowIndex,
    meal,
    field,
    value
  ) => {
    setDetails((previous) => {
      const updated = [
        ...previous,
      ];

      const currentDay = {
        ...updated[rowIndex],
      };

      currentDay[meal] = {
        ...currentDay[meal],

        [field]:
          field === "selected"
            ? Boolean(value)
            : toNumber(value, 0),
      };

      currentDay.totalAmount =
        calculateDayTotal(
          currentDay
        );

      updated[rowIndex] =
        currentDay;

      return updated;
    });
  };

  /* =====================================================
     EXTRA CHANGE
  ===================================================== */

  const handleExtraChange = (
    rowIndex,
    field,
    value
  ) => {
    setDetails((previous) => {
      const updated = [
        ...previous,
      ];

      const currentDay = {
        ...updated[rowIndex],

        [field]:
          field ===
            "extraAmount"
            ? toNumber(value, 0)
            : value,
      };

      currentDay.totalAmount =
        calculateDayTotal(
          currentDay
        );

      updated[rowIndex] =
        currentDay;

      return updated;
    });
  };

  /* =====================================================
     DELETE DAY
  ===================================================== */

  const removeDay = (index) => {
    setDetails((previous) =>
      previous.filter(
        (_, i) => i !== index
      )
    );
  };

  /* =====================================================
     SAVE
  ===================================================== */

  const handleSave = () => {
    if (!onSaveDetails) {
      onClose();
      return;
    }

    const finalDetails =
      details.map((item) => ({
        /*
         * Keep ID for update/edit.
         */

        id: item?.id ?? null,

        hostelId:
          item?.hostelId ??
          rowHostelId ??
          null,

        mealDate:
          item?.date ||
          null,

        /* ================================
           BREAKFAST
        ================================= */

        breakfastSelected:
          Boolean(
            item?.breakfast
              ?.selected
          ),

        breakfastQty:
          toNumber(
            item?.breakfast?.qty,
            0
          ),

        breakfastChildQty:
          toNumber(
            item?.breakfast
              ?.childQty,
            0
          ),

        breakfastAmount:
          toNumber(
            item?.breakfast?.amount,
            0
          ),

        /* ================================
           LUNCH
        ================================= */

        lunchSelected:
          Boolean(
            item?.lunch?.selected
          ),

        lunchQty:
          toNumber(
            item?.lunch?.qty,
            0
          ),

        lunchChildQty:
          toNumber(
            item?.lunch
              ?.childQty,
            0
          ),

        lunchAmount:
          toNumber(
            item?.lunch?.amount,
            0
          ),

        /* ================================
           DINNER
        ================================= */

        dinnerSelected:
          Boolean(
            item?.dinner?.selected
          ),

        dinnerQty:
          toNumber(
            item?.dinner?.qty,
            0
          ),

        dinnerChildQty:
          toNumber(
            item?.dinner
              ?.childQty,
            0
          ),

        /*
         * THIS MUST RECEIVE 2334
         * for your example.
         */

        dinnerAmount:
          toNumber(
            item?.dinner?.amount,
            0
          ),

        /* ================================
           EXTRA
        ================================= */

        extraMeal:
          item?.extraMeal || "",

        extraAmount:
          toNumber(
            item?.extraAmount,
            0
          ),

        /* ================================
           TOTAL
        ================================= */

        totalAmount:
          calculateDayTotal(item),
      }));

    console.log(
      "FINAL DAILY PAYLOAD:",
      JSON.stringify(
        finalDetails,
        null,
        2
      )
    );

    onSaveDetails(finalDetails);

    onClose();
  };

  /* =====================================================
     MEAL CONFIG
  ===================================================== */

  const mealConfig = [
    {
      key: "breakfast",
      label: "Breakfast",
      icon: <FreeBreakfastIcon />,
    },

    {
      key: "lunch",
      label: "Lunch",
      icon: <LunchDiningIcon />,
    },

    {
      key: "dinner",
      label: "Dinner",
      icon: <DinnerDiningIcon />,
    },
  ];

  /* =====================================================
     MEAL CARD
  ===================================================== */

  const renderMealCard = (
    meal,
    index,
    item
  ) => {
    const mealData =
      item?.[meal.key] ||
      createMeal(rowCount);

    return (
      <Grid
        item
        xs={12}
        md={4}
        key={meal.key}
      >
        <Paper
          elevation={0}
          sx={{
            height: "100%",
            border: "1px solid",

            borderColor:
              mealData.selected
                ? "#dbeafe"
                : "#e2e8f0",

            borderRadius: 2,

            backgroundColor:
              mealData.selected
                ? "#fff"
                : "#f8fafc",

            overflow: "hidden",

            transition:
              "all .2s ease",

            "&:hover": {
              borderColor:
                mealData.selected
                  ? "#93c5fd"
                  : "#cbd5e1",
            },
          }}
        >
          {/* MEAL TOP */}

          <Box
            sx={{
              px: 1.25,
              py: 1,

              backgroundColor:
                mealData.selected
                  ? "#f8fbff"
                  : "#f8fafc",

              borderBottom:
                "1px solid #edf2f7",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: 1.5,

                    display: "flex",
                    alignItems:
                      "center",
                    justifyContent:
                      "center",

                    backgroundColor:
                      mealData.selected
                        ? "#eaf3ff"
                        : "#eef2f6",

                    color:
                      mealData.selected
                        ? "primary.main"
                        : "text.disabled",
                  }}
                >
                  {React.cloneElement(
                    meal.icon,
                    {
                      sx: {
                        fontSize: 17,
                      },
                    }
                  )}
                </Box>

                <Typography
                  variant="body2"
                  fontWeight={700}
                >
                  {meal.label}
                </Typography>
              </Stack>

              <FormControlLabel
                sx={{
                  m: 0,

                  "& .MuiFormControlLabel-label":
                    {
                      fontSize:
                        "0.72rem",
                      fontWeight: 600,
                    },
                }}
                control={
                  <Checkbox
                    size="small"
                    checked={Boolean(
                      mealData.selected
                    )}
                    disabled={!isEditing}
                    onChange={(e) =>
                      handleMealChange(
                        index,
                        meal.key,
                        "selected",
                        e.target.checked
                      )
                    }
                    sx={{
                      p: 0.5,
                    }}
                  />
                }
                label="Include"
              />
            </Stack>
          </Box>

          {/* MEAL BODY */}

          <Box
            sx={{
              p: 1.25,
            }}
          >
            <Grid
              container
              spacing={1}
            >
              {/* QTY */}

              <Grid
                item
                xs={4}
              >
                <TextField
                  label="Qty"
                  type="number"
                  size="small"
                  fullWidth
                  inputProps={{
                    min: 0,
                  }}
                  value={
                    mealData.qty ?? 0
                  }
                  disabled={
                    !isEditing ||
                    !mealData.selected
                  }
                  onChange={(e) =>
                    handleMealChange(
                      index,
                      meal.key,
                      "qty",
                      e.target.value
                    )
                  }
                  sx={compactFieldSx}
                />
              </Grid>

              {/* CHILD */}

              <Grid
                item
                xs={4}
              >
                <TextField
                  label="Child"
                  type="number"
                  size="small"
                  fullWidth
                  inputProps={{
                    min: 0,
                  }}
                  value={
                    mealData.childQty ??
                    0
                  }
                  disabled={
                    !isEditing ||
                    !mealData.selected
                  }
                  onChange={(e) =>
                    handleMealChange(
                      index,
                      meal.key,
                      "childQty",
                      e.target.value
                    )
                  }
                  sx={compactFieldSx}
                />
              </Grid>

              {/* AMOUNT */}

              <Grid
                item
                xs={4}
              >
                <TextField
                  label="Amount"
                  type="number"
                  size="small"
                  fullWidth
                  inputProps={{
                    min: 0,
                  }}
                  value={
                    mealData.amount ?? 0
                  }
                  disabled={
                    !isEditing ||
                    !mealData.selected
                  }
                  onChange={(e) =>
                    handleMealChange(
                      index,
                      meal.key,
                      "amount",
                      e.target.value
                    )
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography
                          sx={{
                            fontSize:
                              "0.75rem",
                            color:
                              "text.secondary",
                          }}
                        >
                          ₹
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                  sx={compactFieldSx}
                />
              </Grid>
            </Grid>

            {/* MEAL TOTAL */}

            <Box
              sx={{
                mt: 1,
                px: 1,
                py: 0.7,
                borderRadius: 1.5,

                backgroundColor:
                  mealData.selected
                    ? "#f0fdf4"
                    : "#f8fafc",

                border: "1px solid",

                borderColor:
                  mealData.selected
                    ? "#dcfce7"
                    : "#e5e7eb",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                >
                  Meal Total
                </Typography>

                <Typography
                  variant="body2"
                  fontWeight={800}
                  color={
                    mealData.selected
                      ? "success.dark"
                      : "text.disabled"
                  }
                >
                  ₹
                  {mealData.selected
                    ? toNumber(
                        mealData.amount,
                        0
                      ).toFixed(2)
                    : "0.00"}
                </Typography>
              </Stack>
            </Box>
          </Box>
        </Paper>
      </Grid>
    );
  };

  /* =====================================================
     UI
  ===================================================== */

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          width: "100%",
          maxHeight: "94vh",
          borderRadius: {
            xs: 0,
            sm: 3,
          },
          overflow: "hidden",
          boxShadow:
            "0 24px 70px rgba(15,23,42,.18)",
        },
      }}
    >
      {/* =================================================
          HEADER
      ================================================= */}

      <DialogTitle
        sx={{
          p: 0,
          borderBottom:
            "1px solid #e5e7eb",
          backgroundColor: "#fff",
        }}
      >
        <Box
          sx={{
            px: {
              xs: 2,
              sm: 3,
            },
            py: 1.75,
          }}
        >
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={2}
            alignItems={{
              xs: "flex-start",
              sm: "center",
            }}
            justifyContent="space-between"
          >
            {/* TITLE */}

            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
            >
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2,

                  display: "flex",
                  alignItems:
                    "center",
                  justifyContent:
                    "center",

                  background:
                    "linear-gradient(135deg,#2563eb,#3b82f6)",

                  color: "#fff",

                  boxShadow:
                    "0 5px 14px rgba(37,99,235,.22)",
                }}
              >
                <RestaurantIcon />
              </Box>

              <Box>
                <Typography
                  variant="h6"
                  fontWeight={800}
                  sx={{
                    lineHeight: 1.2,
                  }}
                >
                  Daily Mess Details
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Breakfast, lunch,
                  dinner & daily
                  extra items
                </Typography>
              </Box>
            </Stack>

            {/* ADD DAY */}

            {isEditing && (
              <Button
                variant="contained"
                startIcon={
                  <AddIcon />
                }
                onClick={addDay}
                size="small"
                sx={{
                  borderRadius: 1.75,
                  px: 2,
                  py: 0.85,
                  textTransform:
                    "none",
                  fontWeight: 700,
                  boxShadow: "none",
                  minWidth: {
                    xs: "100%",
                    sm: 110,
                  },
                }}
              >
                Add Day
              </Button>
            )}
          </Stack>
        </Box>
      </DialogTitle>

      {/* =================================================
          CONTENT
      ================================================= */}

      <DialogContent
        sx={{
          p: {
            xs: 1.5,
            sm: 2.5,
          },
          backgroundColor:
            "#f5f7fb",
        }}
      >
        {/* EMPTY */}

        {details.length === 0 && (
          <Paper
            elevation={0}
            sx={{
              py: 7,
              px: 3,
              textAlign: "center",
              borderRadius: 3,
              border:
                "1px dashed #cbd5e1",
              backgroundColor: "#fff",
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                mx: "auto",
                mb: 1.5,
                borderRadius: 2,

                display: "flex",
                alignItems:
                  "center",
                justifyContent:
                  "center",

                backgroundColor:
                  "#eff6ff",

                color:
                  "primary.main",
              }}
            >
              <RestaurantIcon />
            </Box>

            <Typography
              fontWeight={700}
            >
              No mess days available
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
              }}
            >
              Add a day to start
              entering mess
              details.
            </Typography>

            {isEditing && (
              <Button
                variant="contained"
                startIcon={
                  <AddIcon />
                }
                onClick={addDay}
                sx={{
                  mt: 2,
                  borderRadius: 1.75,
                  textTransform:
                    "none",
                  fontWeight: 700,
                }}
              >
                Add First Day
              </Button>
            )}
          </Paper>
        )}

        {/* =================================================
            DAYS
        ================================================= */}

        {details.map(
          (item, index) => (
            <Paper
              key={
                item.id ||
                `${item.date}-${index}`
              }
              elevation={0}
              sx={{
                mb: 2,
                borderRadius: 2.5,
                border:
                  "1px solid #e2e8f0",
                backgroundColor:
                  "#fff",
                overflow: "hidden",
              }}
            >
              {/* DAY HEADER */}

              <Box
                sx={{
                  px: {
                    xs: 1.5,
                    sm: 2,
                  },
                  py: 1.25,
                  backgroundColor:
                    "#fbfcfe",
                  borderBottom:
                    "1px solid #edf0f4",
                }}
              >
                <Grid
                  container
                  spacing={1.5}
                  alignItems="center"
                >
                  {/* DAY */}

                  <Grid
                    item
                    xs={12}
                    sm="auto"
                  >
                    <Chip
                      label={`Day ${
                        index + 1
                      }`}
                      size="small"
                      color="primary"
                      sx={{
                        height: 28,
                        fontWeight: 800,
                        borderRadius: 1.5,
                      }}
                    />
                  </Grid>

                  {/* DATE */}

                  <Grid
                    item
                    xs={12}
                    sm={4}
                    md={2.5}
                  >
                    <TextField
                      type="date"
                      label="Mess Date"
                      fullWidth
                      size="small"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      value={
                        item.date || ""
                      }
                      disabled={!isEditing}
                      onChange={(e) =>
                        handleDateChange(
                          index,
                          e.target.value
                        )
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarMonthIcon
                              sx={{
                                fontSize: 17,
                                color:
                                  "text.secondary",
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                      sx={
                        compactFieldSx
                      }
                    />
                  </Grid>

                  {/* DAY TOTAL */}

                  <Grid
                    item
                    xs={12}
                    sm
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent={{
                        xs: "flex-start",
                        sm: "flex-end",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                      >
                        Day Total
                      </Typography>

                      <Typography
                        variant="body1"
                        fontWeight={800}
                        color="primary.dark"
                      >
                        ₹
                        {toNumber(
                          item.totalAmount,
                          0
                        ).toFixed(2)}
                      </Typography>
                    </Stack>
                  </Grid>

                  {/* DELETE */}

                  {isEditing && (
                    <Grid
                      item
                      xs={12}
                      sm="auto"
                    >
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          removeDay(
                            index
                          )
                        }
                        sx={{
                          border:
                            "1px solid #fecaca",
                          borderRadius: 1.5,
                          width: 34,
                          height: 34,
                        }}
                      >
                        <DeleteOutlineIcon
                          fontSize="small"
                        />
                      </IconButton>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* DAY BODY */}

              <Box
                sx={{
                  p: {
                    xs: 1.25,
                    sm: 1.75,
                  },
                }}
              >
                {/* MEALS */}

                <Grid
                  container
                  spacing={1.25}
                >
                  {mealConfig.map(
                    (meal) =>
                      renderMealCard(
                        meal,
                        index,
                        item
                      )
                  )}
                </Grid>

                {/* EXTRA ITEM */}

                <Paper
                  elevation={0}
                  sx={{
                    mt: 1.5,
                    p: 1.25,
                    borderRadius: 2,
                    border:
                      "1px solid #fde68a",
                    backgroundColor:
                      "#fffdf5",
                  }}
                >
                  <Grid
                    container
                    spacing={1.25}
                    alignItems="center"
                  >
                    {/* LABEL */}

                    <Grid
                      item
                      xs={12}
                      md={2.5}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                      >
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius:
                              1.5,

                            display:
                              "flex",
                            alignItems:
                              "center",
                            justifyContent:
                              "center",

                            backgroundColor:
                              "#fef3c7",

                            color:
                              "#b45309",
                          }}
                        >
                          <AddIcon
                            sx={{
                              fontSize: 17,
                            }}
                          />
                        </Box>

                        <Typography
                          variant="body2"
                          fontWeight={700}
                        >
                          Extra Item
                        </Typography>
                      </Stack>
                    </Grid>

                    {/* EXTRA MEAL */}

                    <Grid
                      item
                      xs={12}
                      md
                    >
                      <TextField
                        label="Extra Meal"
                        placeholder="e.g. Chicken Fry"
                        size="small"
                        fullWidth
                        value={
                          item.extraMeal ||
                          ""
                        }
                        disabled={
                          !isEditing
                        }
                        onChange={(e) =>
                          handleExtraChange(
                            index,
                            "extraMeal",
                            e.target.value
                          )
                        }
                        sx={
                          compactFieldSx
                        }
                      />
                    </Grid>

                    {/* EXTRA AMOUNT */}

                    <Grid
                      item
                      xs={12}
                      md={2}
                    >
                      <TextField
                        label="Extra Amount"
                        type="number"
                        size="small"
                        fullWidth
                        inputProps={{
                          min: 0,
                        }}
                        value={
                          item.extraAmount ??
                          0
                        }
                        disabled={
                          !isEditing
                        }
                        onChange={(e) =>
                          handleExtraChange(
                            index,
                            "extraAmount",
                            e.target.value
                          )
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              ₹
                            </InputAdornment>
                          ),
                        }}
                        sx={
                          compactFieldSx
                        }
                      />
                    </Grid>
                  </Grid>
                </Paper>

                {/* DAY SUMMARY */}

                <Box
                  sx={{
                    mt: 1.25,
                    pt: 1.25,
                    borderTop:
                      "1px dashed #e2e8f0",
                  }}
                >
                  <Stack
                    direction={{
                      xs: "column",
                      sm: "row",
                    }}
                    spacing={1}
                    alignItems={{
                      xs: "flex-start",
                      sm: "center",
                    }}
                    justifyContent="space-between"
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      {item.extraMeal
                        ? `Extra: ${item.extraMeal}`
                        : "No extra item added"}
                    </Typography>

                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.65,
                        borderRadius: 1.5,
                        backgroundColor:
                          "#eff6ff",
                        border:
                          "1px solid #dbeafe",
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={800}
                        color="primary.dark"
                      >
                        Day Total: ₹
                        {toNumber(
                          item.totalAmount,
                          0
                        ).toFixed(2)}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Box>
            </Paper>
          )
        )}

        {/* =================================================
            GRAND TOTAL
        ================================================= */}

        {details.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              mt: 2,
              p: {
                xs: 1.75,
                sm: 2,
              },
              borderRadius: 2.5,
              border:
                "1px solid #bbf7d0",
              background:
                "linear-gradient(135deg,#f0fdf4,#ecfdf5)",
            }}
          >
            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              spacing={1.5}
              alignItems={{
                xs: "flex-start",
                sm: "center",
              }}
              justifyContent="space-between"
            >
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
              >
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: 1.75,

                    display: "flex",
                    alignItems:
                      "center",
                    justifyContent:
                      "center",

                    backgroundColor:
                      "#dcfce7",

                    color:
                      "#15803d",
                  }}
                >
                  <ReceiptLongIcon
                    fontSize="small"
                  />
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    Total Mess Amount
                  </Typography>

                  <Typography
                    variant="h5"
                    fontWeight={900}
                    color="#166534"
                    sx={{
                      lineHeight: 1.1,
                    }}
                  >
                    ₹
                    {toNumber(
                      totalAmount(),
                      0
                    ).toFixed(2)}
                  </Typography>
                </Box>
              </Stack>

              <Chip
                label={`${details.length} Day${
                  details.length > 1
                    ? "s"
                    : ""
                }`}
                size="small"
                sx={{
                  fontWeight: 700,
                  backgroundColor:
                    "#dcfce7",
                  color:
                    "#166534",
                  border:
                    "1px solid #bbf7d0",
                }}
              />
            </Stack>
          </Paper>
        )}
      </DialogContent>

      {/* =================================================
          FOOTER
      ================================================= */}

      <DialogActions
        sx={{
          px: {
            xs: 1.5,
            sm: 2.5,
          },
          py: 1.25,
          borderTop:
            "1px solid #e5e7eb",
          backgroundColor: "#fff",
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          sx={{
            width: "100%",
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            size="small"
            sx={{
              minWidth: 95,
              height: 38,
              borderRadius: 1.75,
              textTransform:
                "none",
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>

          {isEditing && (
            <Button
              variant="contained"
              color="success"
              onClick={handleSave}
              size="small"
              sx={{
                minWidth: 125,
                height: 38,
                borderRadius: 1.75,
                textTransform:
                  "none",
                fontWeight: 700,
                boxShadow: "none",
              }}
            >
              Save Details
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default DailyMessDetailsDialog;